import { useMutation } from '@apollo/client';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import cx from 'classnames';
import { memo, useState } from 'react';
import { connect } from 'react-redux';

import { gengql } from '@/__generated__';
import type { CreditPrice as ApiCreditPrice } from '@/__generated__/graphql';
import { notify } from '@/actions/ui';
import { Money, money } from '@/core/money';
import { RootState } from '@/store';

import Button from '../Button';
import Link from '../Link';
import s from './CreditPurchaseForm.module.scss';

const ENSURE_USER_BILLING_ACCOUNT = gengql(/* GraphQL */ `
  mutation creditPurchaseFormensureUserBillingAccount($userId: String) {
    ensureUserBillingAccount(user_id: $userId) {
      id
    }
  }
`);

const CREATE_CHECKOUT_URL = gengql(/* GraphQL */ `
  mutation creditPurchaseFormCreateCheckoutURL(
    $accountId: String!
    $priceId: String!
    $quantity: Int
    $path: String!
  ) {
    createCheckoutURL(account_id: $accountId, price_id: $priceId, quantity: $quantity, path: $path)
  }
`);

// Local view-model with `total` aliasing the API's `money` field — clearer at
// the call sites where we render an Order Total. Wrapped Money values come from
// the money() factory; the underlying contract is the generated CreditPrice.
interface Price {
  id: string;
  credits: Money;
  total: Money;
  pricePerCredit: Money;
}

interface Account {
  id: string;
  credit_balance: Money;
}

type CreditPurchaseFormProps = {
  prices: Price[];
  account: Account;
  purchaseDonePath: string;
};

export const CreditPurchaseFormPure = memo(
  ({ prices, account, purchaseDonePath }: CreditPurchaseFormProps) => {
    // Derive the sorted list and the 1-credit unit tier locally. The API
    // resolver currently sorts ascending and errors when no unit tier is
    // present, but the GraphQL schema and CreditPricesContract (api/tla)
    // only guarantee presence — not ordering. Deriving here keeps custom-
    // amount math and the `quantity` Int correct even if the resolver's
    // ordering changes.
    const sortedPrices = [...prices].sort((a, b) => a.credits.cents - b.credits.cents);
    const unitPrice = sortedPrices.find((p) => p.credits.cents === 100);
    if (!unitPrice) {
      throw new Error('CreditPurchaseForm: prices missing the 1-credit unit tier');
    }

    const [{ priceId, customAmount }, selectOption] = useState<{
      priceId: string;
      customAmount?: Money;
    }>({
      priceId: unitPrice.id,
      customAmount: undefined,
    });

    const [ensureBillingAccount] = useMutation(ENSURE_USER_BILLING_ACCOUNT);
    const [createCheckoutUrl] = useMutation(CREATE_CHECKOUT_URL);

    const balance = account?.credit_balance || money({ currency: 'OFC', cents: 0 });

    const selected = sortedPrices.find((p) => p.id === priceId) || unitPrice;
    const amount = priceId === 'custom' ? customAmount : selected.credits;

    // Selected packages: use Stripe's package total verbatim — authoritative,
    // cannot drift from what Stripe will charge. Custom amounts multiply the
    // (whole-dollar) unit rate by credit count, so no rounding drift.
    const actualCost =
      priceId === 'custom'
        ? amount && unitPrice.pricePerCredit.times(amount.cents / 100)
        : selected.total;

    const nominalCost = amount && unitPrice.pricePerCredit.times(amount.cents / 100);
    const discount = actualCost && nominalCost && actualCost.subtract(nominalCost);
    const discountPercentage =
      discount && nominalCost && nominalCost.cents > 0
        ? Math.round((discount.cents / nominalCost.cents) * -100)
        : 0;

    const step = unitPrice.credits.cents / 100;

    const handleSelect = (event: any) => {
      selectOption({ priceId: event.target.value, customAmount });
    };

    const handlePurchaseClick = async () => {
      const quantity = amount && amount.div(selected.credits);
      const checkoutPriceId = priceId === 'custom' ? unitPrice.id : priceId;

      let accountId = account.id;
      // First query: Ensure the user has a billing account
      if (!accountId) {
        const { data } = await ensureBillingAccount();
        accountId = data!.ensureUserBillingAccount!.id;
      }

      // Second query: Create the checkout URL using the accountId
      const { data: checkoutData } = await createCheckoutUrl({
        variables: {
          accountId,
          quantity,
          priceId: checkoutPriceId,
          path: purchaseDonePath,
        },
      });

      const url = checkoutData!.createCheckoutURL!;
      window.location.assign(url);
    };

    return (
      <div>
        <div className={s.balance}>
          Your current balance is&nbsp;
          <span
            className={cx({
              [s.balanceWarning]: balance.cents <= 0,
            })}
          >
            {money(balance).formatted()}
          </span>
          .
        </div>

        <div className={s.amount}>
          <RadioGroup value={priceId} onChange={handleSelect}>
            {sortedPrices.map((p) => (
              <FormControlLabel
                key={p.id}
                value={p.id}
                control={<Radio />}
                style={{ marginBottom: 10 }}
                label={`${p.credits.formatted()}: ${p.pricePerCredit.formatted({
                  omitEmptyCents: true,
                })}/credit`}
              />
            ))}

            <div className="MuiFormControlLabel-root">
              <Radio
                key="custom"
                value="custom"
                // style={{ display: 'inline-block', width: 100 }}
              />
              <TextField
                id="customCredits"
                placeholder="Custom amount"
                value={customAmount === undefined ? '' : customAmount.cents / 100}
                margin="none"
                fullWidth={false}
                type="number"
                inputProps={{
                  min: 1,
                  max: 1000,
                  step,
                  style: { width: '200px' },
                }}
                onClick={() => {
                  selectOption({
                    priceId: 'custom',
                    customAmount,
                  });
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  const s = e.target.value || '';
                  if (s.length > 3 || s.includes('-')) return;
                  const value = Math.max(0, Math.min(1000, parseInt(s)));
                  const credits = Number.isNaN(value) ? undefined : money(value * 100, 'OFC');
                  selectOption({ priceId: 'custom', customAmount: credits });
                }}
              />
            </div>
          </RadioGroup>
        </div>

        <div className={s.order}>
          <div className={s.orderTitle}>Your Order</div>
          <hr />
          <div className={s.orderRow}>
            <div className={s.orderCell}>{amount ? amount.formatted() : '0 credits'}</div>
            <div className={s.orderShortCell}>
              {nominalCost ? nominalCost.formatted() : '$0.00'}
            </div>
          </div>
          <div
            className={s.orderRow}
            style={{
              visibility: discountPercentage ? 'visible' : 'hidden',
            }}
          >
            <div className={cx(s.orderCell, s.orderDiscount)}>{discountPercentage}% Discount</div>
            <div className={s.orderShortCell}>{discount && discount.formatted()}</div>
          </div>
          <hr />
          <div className={s.orderRow}>
            <div className={s.orderCell}>Total</div>
            <div className={s.orderShortCell}>{actualCost ? actualCost.formatted() : '$0.00'}</div>
          </div>
        </div>
        <div className={s.footer}>
          <div className={cx(s.footerComponent, s.footerCard)} />
          <Button
            className={s.footerComponent}
            disabled={!actualCost || !amount || amount.cents === 0 || actualCost.cents > 99999999}
            onClick={handlePurchaseClick}
            //primary
            type="submit"
            size="large"
            label="Purchase"
          />
        </div>
        <div className={s.questions}>
          For large purchases, please contact our{' '}
          <Link href="mailto:hello@onfrontiers.com">customer care team</Link>.
        </div>
        {/* For reference: there is PaymentUpdate component in git history */}
      </div>
    );
  }
);
CreditPurchaseFormPure.displayName = 'CreditPurchaseFormPure';

export default connect(
  (state: RootState, props: CreditPurchaseFormProps) => {
    if (!state.billing) throw new Error('billing not found in state');
    const { account } = props;
    const { prices: rawPrices } = state.billing;

    if (!rawPrices) throw new Error('billing prices not found in state');

    // creditPrices is the typed contract — credits/money/pricePerCredit are
    // schema-guaranteed non-null and the API errors out server-side when no
    // 1-credit unit tier is configured. See api/tla/BillingClassification.tla
    // (CreditPricesContract) and api/billing/service/stripe.go FindCreditPrices.
    const prices: Price[] = (rawPrices as ApiCreditPrice[]).map((raw) => ({
      id: raw.id,
      credits: money(raw.credits),
      total: money(raw.money),
      pricePerCredit: money(raw.pricePerCredit),
    }));

    if (!prices.length) throw new Error('at least one price must be supplied');

    const { viewer } = state;

    return { viewer, account, prices };
  },
  {
    notify,
  }
)(CreditPurchaseFormPure);
